import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Card, Col, Row } from 'react-bootstrap';
import useViewport from 'utils/useViewportHelper';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import Spinner from 'components/Spinner';
import { useNavigate } from 'react-router-dom';
import { RENEWAL } from 'constants/routeConstants';
import useReferrerParam from 'hooks/useReferrerParam';
import { PaymentError } from 'types/expiryRenewal';
import { Store } from 'types/store';
import useFetchPaymentData from './hooks/useFetchPaymentData';
import CardPaymentForm from './components/organisms/CardPaymentForm';
import Stepper from './components/organisms/Stepper';
import Banner from './components/molecules/Banner';
import PaymentHeader from './components/molecules/PaymentHeader';
import TimeoutWarningModal from './components/molecules/TimeoutWarningModal';

import './expiryRenewalGlobals.scss';

interface Props {
  usePreviousCardDetails?: boolean;
}

const Payment = ({ usePreviousCardDetails = false }: Props) => {
  const { pageModel, paymentData } = useSelector((state: Store) => state.expiryRenewal);
  const navigate = useNavigate();

  const [error, setError] = useState<PaymentError | null>(null);

  const { isMobile } = useViewport();

  const isAppView = useReferrerParam();

  const { loading } = useFetchPaymentData({ setErrorModal: setError, usePreviousCardDetails });

  const { cosmosTrackEvent } = useCosmosTrackEvent('ExpiryRenewals');

  useEffect(() => {
    cosmosTrackEvent(usePreviousCardDetails ? 'renewal_journey_previous_payment' : 'renewal_journey_payment');
  }, []);

  const price = paymentData?.price || null;
  const discountedPrice = paymentData?.discountModel?.originalPrice || null;
  const isDiscountedPrice = paymentData?.discountModel?.isDiscountedPrice;

  useEffect(() => {
    if (!pageModel) navigate(RENEWAL, { replace: true });
  }, [pageModel, navigate]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center bg-grey">
        {isMobile ? <Spinner mobileSpinner /> : <Spinner bootSpinner />}
      </div>
    );
  }

  return (
    <>
      {pageModel?.discountModel?.isDiscountedPrice && (
        <Banner />
      )}
      <Stepper step={4} />
      <div className={clsx('fs-6 mb-5 text-secondary', isAppView ? 'm-3 p-3 bg-white border rounded' : 'p-4')}>
        {pageModel && (
          <PaymentHeader
            smallHeader={pageModel?.isMonthlyLongerTermPolicy ? 'Pay monthly' : 'Pay annually'}
            mainHeader="One-time card payment"
            description={pageModel?.isMonthlyLongerTermPolicy && 'We can’t always split the cost of your cover into equal monthly payments.'}
            price={price}
            priceWas={isDiscountedPrice && discountedPrice}
          />
        )}
        <Card className="border border-light shadow-sm bg-body rounded">
          <Row>
            <Col>
              <CardPaymentForm header={usePreviousCardDetails ? 'Use previous payment details' : 'Add a new card'} error={error} />
            </Col>
          </Row>
        </Card>
      </div>
      <TimeoutWarningModal />
    </>
  );
}

export default Payment;
